/**
 * DentalPost promo campaigns (DP-2225).
 *
 * Content is authored by marketing in the "DentalPost Promos" HubDB table and
 * baked into the bundle at build time (see gatsby-node.ts `sourceNodes`). A
 * campaign can drive a top-nav banner, a first-visit pop-up, or both.
 *
 * All visibility decisions run client-side at render time — never pre-filtered
 * at build — so a shipped Capacitor bundle correctly expires a campaign on its
 * end date without needing an app-store update.
 */
import dayjs from 'dayjs';

export type Promo = {
  promoId: string;
  isActive: boolean;
  showBanner: boolean;
  showPopup: boolean;
  startDate: string | null; // ISO
  endDate: string | null; // ISO
  chipLabel: string;
  modalHeading: string;
  modalBody: string;
  bullets: string[];
  bannerHeading: string;
  ctaLabel: string;
  // The banner, pop-up, and salary-sidebar card each carry their own
  // destination so marketing can add surface-specific tracking params; all
  // fall back to the shared cta_url column at build time (see mapPromoRow).
  bannerCtaUrl: string;
  popupCtaUrl: string;
  sidebarCtaUrl: string;
  secondaryLabel: string;
  frequencyDays: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Local-time `YYYY-MM-DD` key for a timestamp. */
export function dateKey(ms: number): string {
  return dayjs(ms).format('YYYY-MM-DD');
}

/** Whole calendar days between a `YYYY-MM-DD` key and a timestamp (local). */
function daysSinceKey(key: string, nowMs: number): number {
  const then = dayjs(`${key}T00:00:00`);
  if (!then.isValid()) return Infinity;
  // Round the ms delta (rather than dayjs' truncating .diff) so a DST hour
  // can't push a whole-day gap to one short.
  const nowMidnight = dayjs(nowMs).startOf('day');
  return Math.round(nowMidnight.diff(then) / DAY_MS);
}

/**
 * Is the campaign live right now? Active flag on, a usable CTA, and today
 * within [startDate, endDate]. endDate is inclusive of its whole day.
 */
export function isCampaignLive(promo: Promo | null, nowMs: number): boolean {
  if (!promo || !promo.isActive) return false;
  if (!promo.bannerCtaUrl && !promo.popupCtaUrl) return false;
  const now = dayjs(nowMs);
  if (promo.startDate) {
    const start = dayjs(promo.startDate);
    if (start.isValid() && now.isBefore(start)) return false;
  }
  if (promo.endDate) {
    const end = dayjs(promo.endDate);
    // endDate is inclusive of its whole day, so the campaign runs until the
    // day after it.
    if (end.isValid() && now.isAfter(end.add(1, 'day'))) return false;
  }
  return true;
}

export function isBannerConfigured(promo: Promo | null): boolean {
  return (
    !!promo &&
    promo.isActive &&
    !!promo.showBanner &&
    !!promo.bannerHeading &&
    !!promo.bannerCtaUrl
  );
}

export function shouldShowBanner(promo: Promo | null, nowMs: number): boolean {
  return isBannerConfigured(promo) && isCampaignLive(promo, nowMs);
}

/**
 * Pop-up shows when the campaign is live, opted in, has content, and hasn't
 * been shown within its `frequencyDays` window (default once per day).
 */
export function shouldShowPopup(
  promo: Promo | null,
  opts: { nowMs: number; lastShownKey: string | null }
): boolean {
  if (!isCampaignLive(promo, opts.nowMs)) return false;
  if (!promo!.showPopup || !promo!.modalHeading || !promo!.popupCtaUrl)
    return false;
  if (!opts.lastShownKey) return true;
  const freq = Math.max(1, promo!.frequencyDays || 1);
  return daysSinceKey(opts.lastShownKey, opts.nowMs) >= freq;
}

// ── localStorage (cross-session; each campaign tracked by its promoId) ───────

const POPUP_CAMPAIGN_KEY = 'dp_promo_popup_campaign';
const POPUP_LAST_SHOWN_KEY = 'dp_promo_popup_last_shown';

/**
 * The `YYYY-MM-DD` the pop-up was last shown for this campaign, or null if it
 * was last shown for a different campaign (so a new promo starts fresh).
 */
export function getPopupLastShown(promoId: string): string | null {
  try {
    if (localStorage.getItem(POPUP_CAMPAIGN_KEY) !== promoId) return null;
    return localStorage.getItem(POPUP_LAST_SHOWN_KEY);
  } catch {
    return null;
  }
}

export function setPopupLastShown(promoId: string, key: string): void {
  try {
    localStorage.setItem(POPUP_CAMPAIGN_KEY, promoId);
    localStorage.setItem(POPUP_LAST_SHOWN_KEY, key);
  } catch {}
}
